import React, { useState } from 'react';
import { Col } from 'react-bootstrap';
import Button from '../../../../../components/button';
import {
  ApprovalRequestStatus,
  useApprovalRequestUpdateStatusMutation,
} from '../../../__generated__/graphql';
import { ApprovalRequestCommentModal } from './ApprovalRequestCommentModal';
import { useToasts } from '../../../../../context/Toast';
import { useMutationErrorHandler } from '../../../../../hooks/useMutationErrorHandler';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';

type Props = {
  id: string;
  relatedRecordId: string;
};

export const ReviewActionsCell: React.FC<Props> = ({ id, relatedRecordId }) => {
  const [updateStatus] = useApprovalRequestUpdateStatusMutation();
  const [isAccepting, setIsAccepting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [rejectingModalId, setRejectingModalId] = useState<string | null>(null);
  const isInProgress = isAccepting || isRejecting;

  const { addSuccess } = useToasts();
  const errorHandler = useMutationErrorHandler();
  const t = useNamespacedTranslation('approvalRequest');

  const handleAccept = () => {
    setIsAccepting(true);
    updateStatus({
      variables: {
        id,
        status: ApprovalRequestStatus.Approved,
        comment: '',
        relatedRecordId,
      },
    })
      .then(() => {
        setIsAccepting(false);
        addSuccess(t('approvedSuccessToast'));
      })
      .catch((error) => {
        setIsAccepting(false);
        errorHandler(error);
      });
  };

  const handleReject = (requestId: string, reason: string | null) => {
    setIsRejecting(true);
    setRejectingModalId(null);
    updateStatus({
      variables: {
        id: requestId,
        status: ApprovalRequestStatus.Rejected,
        comment: reason || '',
        relatedRecordId,
      },
    })
      .then(() => {
        setIsRejecting(false);
        addSuccess(t('rejectedSuccessToast'));
      })
      .catch((error) => {
        setIsRejecting(false);
        errorHandler(error);
      });
  };

  const handleRejectButtonClick = () => {
    setRejectingModalId(id);
  };

  const onModalClose = () => {
    setRejectingModalId(null);
  };

  return (
    <>
      <Col className="col-auto">
        <Button
          label={t('table.actions.approve')}
          isProcessing={isAccepting}
          disabled={isInProgress}
          variant="outline-primary"
          size="sm"
          icon="checkmark-outline"
          onClick={(e) => {
            e.preventDefault();
            handleAccept();
          }}></Button>
        <Button
          label={t('table.actions.reject')}
          isProcessing={isRejecting}
          disabled={isInProgress}
          icon="close-outline"
          variant="outline-danger"
          className="ms-2"
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            handleRejectButtonClick();
          }}></Button>
      </Col>
      <ApprovalRequestCommentModal
        requestId={rejectingModalId}
        inProgress={isRejecting}
        onClose={onModalClose}
        onRejected={handleReject}
      />
    </>
  );
};
